import type { FC, ReactNode } from 'react'
import type { Article } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { Check, Link2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Rule } from '@/components/generals/rule'

interface ArticleBodyProps {
    article: Article
}

const PROSE = 't-cond-soft max-w-[68ch] text-[17px] leading-[1.6] text-ash'

const SHARE = 'group/roll t-mono inline-flex w-fit items-center gap-2 rounded-full border border-ink/15 px-3 py-2 text-ink transition-colors duration-300 hover:border-brand hover:text-brand'

export const ArticleBody: FC<ArticleBodyProps> = ({ article }): ReactNode => {
    /* Empty until mount so the server and the client agree on the first paint,
       then filled from the real address once hydration has finished. */
    const [shareUrl, setShareUrl] = useState('')
    const [copied, setCopied] = useState(false)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        setShareUrl(window.location.href)
    }, [article.slug])

    useEffect(() => {
        return () => {
            if (timer.current) {
                clearTimeout(timer.current)
            }
        }
    }, [])

    const onCopy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(window.location.href)

            setCopied(true)

            if (timer.current) {
                clearTimeout(timer.current)
            }

            timer.current = setTimeout(() => setCopied(false), 2200)
        } catch {
            setCopied(false)
        }
    }

    const [opening, ...rest] = article.body
    const lead = opening ?? ''
    const dropCap = lead.slice(0, 1)
    const leadRest = lead.slice(1)

    return (
        <section
            id='article-body'
            className='mx-auto w-full max-w-[1920px] px-5 sec lg:px-[33px]'
        >
            <div className='grid gap-8 lg:grid-cols-4 lg:gap-5'>
                {/* Column one: the rail that stays with the reader. */}
                <div className='lg:col-span-1'>
                    <div className='flex flex-col gap-5 lg:sticky lg:top-[calc(var(--nav-h)_+_24px)]'>
                        <div className='flex flex-col gap-2'>
                            <span className='t-mono text-ash'>
                                filed under
                            </span>

                            <span className='t-mono text-brand'>
                                {article.category}
                            </span>
                        </div>

                        <Rule />

                        <div className='flex flex-col gap-2'>
                            <span className='t-mono text-ash'>
                                published
                            </span>

                            <time dateTime={article.datetime} className='t-mono text-ink'>
                                {article.date}
                            </time>
                        </div>

                        <Rule />

                        <div className='flex flex-col gap-3'>
                            <span className='t-mono text-ash'>
                                share
                            </span>

                            <div className='flex flex-wrap items-center gap-2'>
                                <a
                                    href={`https://x.com/intent/post?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`}
                                    target='_blank'
                                    rel='noreferrer'
                                    data-cursor='link'
                                    className={SHARE}
                                >
                                    X
                                </a>

                                <a
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                    target='_blank'
                                    rel='noreferrer'
                                    data-cursor='link'
                                    className={SHARE}
                                >
                                    LinkedIn
                                </a>

                                <button
                                    type='button'
                                    data-cursor='link'
                                    onClick={() => {
                                        void onCopy()
                                    }}
                                    className={cn(SHARE, copied && 'border-brand text-brand')}
                                >
                                    {copied
                                        ? <Check className='size-4' aria-hidden='true' />
                                        : <Link2 className='size-4' aria-hidden='true' />}

                                    <span aria-live='polite'>
                                        {copied ? 'Copied' : 'Copy link'}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Columns two to four: the prose itself. */}
                <div className='flex flex-col gap-8 lg:col-span-3 lg:gap-12'>
                    <Reveal from='up' distance={30} duration={1.15} start='top 88%'>
                        <p
                            className='t-cond max-w-[40ch] leading-[1.08] text-ink'
                            style={{ fontSize: 'clamp(21px, 2.2vw, 30px)' }}
                        >
                            {article.excerpt}
                        </p>
                    </Reveal>

                    <Reveal
                        childSelector='[data-prose]'
                        from='up'
                        distance={24}
                        duration={1}
                        stagger={0.12}
                        start='top 86%'
                        className='flex flex-col gap-6'
                    >
                        <p data-prose className={PROSE}>
                            <span className='t-display float-left mr-[0.14em] mt-[0.1em] text-[54px] leading-[0.7] text-ink'>
                                {dropCap}
                            </span>

                            {leadRest}
                        </p>

                        {rest.map((paragraph) => (
                            <p key={paragraph.slice(0, 32)} data-prose className={PROSE}>
                                {paragraph}
                            </p>
                        ))}
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
